import { useEffect, useState, type MouseEvent } from "react";
import { ExternalLink, LockKeyhole, MoreHorizontal, Share2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { isInAppBrowser, OPEN_IN_APP_BROWSER_WARNING_EVENT } from "@/lib/in-app-browser";
import { getStoredReferral } from "@/lib/boide-api";
import logoIcon from "@/assets/logo-icon.png";

function getInAppBrowserName(userAgent: string) {
  if (/Instagram/i.test(userAgent)) return "Instagram";
  if (/Barcelona|Threads/i.test(userAgent)) return "Threads";
  if (/FBAN|FBAV|FB_IAB|MessengerLite/i.test(userAgent)) return "Facebook/Messenger";
  if (/Zalo/i.test(userAgent)) return "Zalo";
  if (/BytedanceWebview|musical_ly|TikTok/i.test(userAgent)) return "TikTok";
  if (/Telegram/i.test(userAgent)) return "Telegram";
  if (/Line\//i.test(userAgent)) return "Line";
  if (/MicroMessenger|WeChat/i.test(userAgent)) return "WeChat";
  return "ứng dụng này";
}

function isIos(userAgent: string) {
  return /iPhone|iPad|iPod/i.test(userAgent);
}

function isAndroid(userAgent: string) {
  return /Android/i.test(userAgent);
}

function getDevPreviewUserAgent() {
  if (!import.meta.env.DEV || typeof window === "undefined") return "";
  const preview = new URLSearchParams(window.location.search).get("iab-preview");
  if (preview === "ios") return "Mozilla/5.0 (iPhone) Instagram";
  if (preview === "android") return "Mozilla/5.0 (Linux; Android 14; wv) FB_IAB";
  return "";
}

/**
 * 🔴 Gắn lại `?ref=` vào URL sẽ mở ở trình duyệt ngoài.
 *
 * `__root.tsx` lưu mã từ `?ref=` rồi XOÁ tham số khỏi URL ngay lập tức, nên lúc popup
 * này hiện ra `location.href` đã sạch mã. localStorage của trình duyệt trong app
 * (Facebook/Zalo/TikTok…) KHÔNG sang được Chrome/Safari, nên không gắn lại ở đây thì
 * người được giới thiệu mở trình duyệt ngoài, đăng ký, và người giới thiệu mất công —
 * đúng ca "bấm link mạng xã hội không ăn mã".
 */
function withReferral(url: string) {
  const code = getStoredReferral();
  if (!code) return url;
  try {
    const u = new URL(url);
    if (!u.searchParams.get("ref")) u.searchParams.set("ref", code);
    return u.toString();
  } catch {
    return url;
  }
}

function buildExternalBrowserHref(currentUrl: string, userAgent: string) {
  if (isAndroid(userAgent)) {
    const url = new URL(currentUrl);
    const scheme = url.protocol.replace(":", "");
    const destination = `${url.host}${url.pathname}${url.search}${url.hash}`;
    return `intent://${destination}#Intent;scheme=${scheme};package=com.android.chrome;S.browser_fallback_url=${encodeURIComponent(currentUrl)};end`;
  }

  if (isIos(userAgent)) {
    // Chrome iOS nhận URL bằng cách thay scheme http/https tương ứng.
    // Không dùng googlechrome://navigate?url=... vì iOS sẽ hiểu "navigate" là hostname.
    return currentUrl.replace(/^https:/i, "googlechromes:").replace(/^http:/i, "googlechrome:");
  }

  return currentUrl;
}

export function InAppBrowserWarning() {
  const [open, setOpen] = useState(false);
  const [userAgent, setUserAgent] = useState("");
  const [currentUrl, setCurrentUrl] = useState("");

  useEffect(() => {
    const show = (previewUserAgent = "") => {
      const nextUserAgent = previewUserAgent || navigator.userAgent || "";
      setUserAgent(nextUserAgent);
      setCurrentUrl(withReferral(window.location.href));
      setOpen(true);
    };

    const previewUserAgent = getDevPreviewUserAgent();
    if (previewUserAgent || isInAppBrowser()) show(previewUserAgent);
    const handleOpenRequest = () => show();
    window.addEventListener(OPEN_IN_APP_BROWSER_WARNING_EVENT, handleOpenRequest);
    return () => window.removeEventListener(OPEN_IN_APP_BROWSER_WARNING_EVENT, handleOpenRequest);
  }, []);

  const ios = isIos(userAgent);
  const android = isAndroid(userAgent);
  const externalHref = currentUrl ? buildExternalBrowserHref(currentUrl, userAgent) : "#";
  const browserLabel = ios || android ? "Chrome" : "trình duyệt ngoài";

  const handleOpenExternalBrowser = async (event: MouseEvent<HTMLAnchorElement>) => {
    if (!ios) return;

    // Messenger/Safari View Controller có thể chặn googlechrome(s)://. Dùng
    // menu chia sẻ native trước để user chọn “Mở bằng trình duyệt bên ngoài”.
    event.preventDefault();
    if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
      try {
        await navigator.share({ url: currentUrl });
      } catch (error) {
        const errorName =
          typeof error === "object" && error !== null && "name" in error
            ? (error as { name?: unknown }).name
            : undefined;
        if (errorName !== "AbortError") window.location.assign(externalHref);
      }
      return;
    }

    window.location.assign(externalHref);
  };

  return (
    <Dialog open={open}>
      <DialogContent
        hideClose
        className="max-h-[92dvh] w-[calc(100%-1.5rem)] max-w-md overflow-y-auto rounded-[1.75rem] border border-amber-500/25 bg-[#fffaf1] px-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] pt-7 text-[#241b13] shadow-[0_24px_80px_rgba(0,0,0,0.45)] sm:px-7"
      >
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl border border-amber-200 bg-white p-1 shadow-[0_10px_30px_rgba(217,154,55,0.2)]">
          <img src={logoIcon} alt="Bói Đê" className="h-12 w-12 object-contain" />
        </div>

        <div className="text-center">
          <DialogTitle className="font-serif-soft text-2xl font-semibold leading-tight text-[#241b13]">
            Mở bằng trình duyệt ngoài
          </DialogTitle>
          <DialogDescription className="mt-3 text-sm leading-6 text-[#67594c]">
            Bạn đang xem Bói Đê trong trình duyệt của {getInAppBrowserName(userAgent)}. Một số tính
            năng, đặc biệt là đăng nhập Google, có thể không hoạt động tại đây.
          </DialogDescription>
        </div>

        <div className="rounded-2xl border border-[#e7d8c4] bg-white/85 p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#fff1d7] text-amber-700">
              <LockKeyhole aria-hidden="true" className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <p className="text-xs font-medium uppercase tracking-[0.12em] text-[#9a7e5e]">
                Trang an toàn
              </p>
              <p className="truncate text-sm font-semibold text-[#34271d]">
                {currentUrl ? new URL(currentUrl).host : "boide.online"}
              </p>
            </div>
          </div>
        </div>

        <div className="rounded-2xl bg-[#f4eadc] px-4 py-3.5 text-sm leading-5 text-[#5c4d3e]">
          <div className="flex items-start gap-3">
            {ios ? (
              <Share2 aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 text-amber-700" />
            ) : (
              <MoreHorizontal
                aria-hidden="true"
                className="mt-0.5 h-5 w-5 shrink-0 text-amber-700"
              />
            )}
            <p>
              {ios
                ? "Trên iPhone, nút bên dưới sẽ mở menu chia sẻ. Hãy chọn “Mở bằng trình duyệt bên ngoài” để tiếp tục bằng Chrome."
                : "Nếu nút bên dưới không mở được, hãy nhấn dấu ⋮ hoặc ••• của ứng dụng rồi chọn “Mở trong Chrome”."}
            </p>
          </div>
        </div>

        <a
          href={externalHref}
          onClick={handleOpenExternalBrowser}
          target={ios || android ? undefined : "_blank"}
          rel="noreferrer"
          className="inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-amber-500 to-amber-600 px-5 py-3.5 text-center text-sm font-bold text-[#241608] shadow-[0_10px_24px_rgba(203,132,30,0.3)] transition hover:brightness-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-600 focus-visible:ring-offset-2 active:scale-[0.98]"
        >
          <ExternalLink aria-hidden="true" className="h-5 w-5" />
          Mở bằng {browserLabel}
        </a>
      </DialogContent>
    </Dialog>
  );
}
